import { formatAccountType } from '@/modules/projects/labels'
import type {
  DirectoryAccount,
  Transaction,
} from '@/modules/projects/stores/types'
import { countsAsMoneyFlow } from '@/modules/projects/utils'

// An open record, because useSort reads whichever column key the header was clicked with.
export interface AccountRow extends Record<string, unknown> {
  number: string
  last4: string
  shareNumber: string | null
  type: string
  owner: string | null
  institution: string | null
  inflows: number
  outflows: number
  count: number
}

// One row per account, or per share when a credit union prints several shares on one last4.
export const foldAccounts = (transactions: Transaction[]): AccountRow[] => {
  const accounts = new Map<string, AccountRow>()

  transactions.forEach((transaction) => {
    const number = transaction.shareNumber
      ? `${transaction.accountNumber} ID: ${transaction.shareNumber}`
      : transaction.accountNumber

    let account = accounts.get(number)
    if (!account) {
      account = {
        number,
        last4: transaction.accountNumber,
        shareNumber: transaction.shareNumber ?? null,
        type: transaction.accountType,
        owner: transaction.accountOwner,
        institution: transaction.institution,
        inflows: 0,
        outflows: 0,
        count: 0,
      }
      accounts.set(number, account)
    }

    // Every row counts, but sweeps and securities trades are not money in or out.
    if (countsAsMoneyFlow(transaction)) {
      if (transaction.amount > 0) {
        account.inflows += transaction.amount
      } else {
        account.outflows += Math.abs(transaction.amount)
      }
    }
    account.count += 1
  })

  return Array.from(accounts.values())
}

// Two real accounts at one bank can print the same last4. lukita refuses to auto-resolve that
// collision, so drop both entries here and let the colliding rows keep their folded labels.
export const directoryByLast4 = (entries: DirectoryAccount[]) => {
  const byLast4 = new Map<string, DirectoryAccount>()
  const collisions = new Set<string>()

  entries.forEach((entry) => {
    if (byLast4.has(entry.last4)) collisions.add(entry.last4)
    byLast4.set(entry.last4, entry)
  })

  collisions.forEach((last4) => byLast4.delete(last4))
  return byLast4
}

// The directory holds one type per last4, so on a credit union printing several shares under one
// number it can only be right about one of them. A share keeps its folded type unless the securities
// side proved the directory's off a real holdings table.
const directoryType = (account: AccountRow, entry: DirectoryAccount) => {
  if (account.shareNumber && !entry.typeAuthoritative) return ''
  return formatAccountType(entry.accountType)
}

// The directory is the project-wide vote on who owns an account, so it beats a label folded from
// whichever row sorted first. An account the directory never saw keeps its folded labels. An account
// the directory knows but has no owner for shows empty, rather than borrowing a row's guess: that
// borrowing is how one "UNKNOWN" row used to rename a whole account.
export const withDirectoryLabels = (
  account: AccountRow,
  directory: Map<string, DirectoryAccount>,
): AccountRow => {
  const entry = directory.get(account.last4)
  if (!entry) return account

  return {
    ...account,
    owner: entry.owner,
    institution: entry.institution || account.institution,
    type: directoryType(account, entry) || account.type,
  }
}
